import React, { useState, useEffect } from 'react';
import { Upload, Save, AlertCircle, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const BANNER_LABELS = {
  home: 'Home Hero Banner',
  menu: 'Menu Page Banner',
  combos: 'Combos Page Banner',
  reviews: 'Reviews Page Banner',
  contact: 'Contact Page Banner',
  combo: 'Combo Promo Banner (Homepage)',
};

const MediaBranding = () => {
  const { authHeader } = useAuth();
  const { refetchSettings } = useSettings();
  const [settings, setSettings] = useState(null);
  const [banners, setBanners] = useState([]);
  const [categories, setCategories] = useState([]);
  const [logoSaving, setLogoSaving] = useState(false);
  const [bannerSaving, setBannerSaving] = useState({});
  const [bannerTextSaving, setBannerTextSaving] = useState({});
  const [bannerTexts, setBannerTexts] = useState({});
  const [catSaving, setCatSaving] = useState({});
  const [logoError, setLogoError] = useState('');

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/settings`),
      axios.get(`${API}/banners`),
      axios.get(`${API}/categories`),
    ]).then(([sRes, bRes, cRes]) => {
      setSettings(sRes.data);
      setBanners(bRes.data);
      setCategories(cRes.data);
      // initialise local text state from DB values
      const texts = {};
      bRes.data.forEach(b => {
        texts[b._id] = { headline: b.headline || '', subtext: b.subtext || '', ctaText: b.ctaText || '' };
      });
      setBannerTexts(texts);
    }).catch(console.error);
  }, []);

  const toBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = ev => resolve(ev.target.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoSaving(true);
    setLogoError('');
    try {
      const base64 = await toBase64(file);
      const { data } = await axios.put(`${API}/settings`, { logo: base64 }, { headers: authHeader });
      if (!data || data.message) throw new Error(data?.message || 'Save failed');
      setSettings(s => ({ ...s, logo: base64 }));
      refetchSettings();
      toast.success('Logo updated! Visible everywhere now.');
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to save logo';
      setLogoError(msg);
      toast.error(msg);
    } finally {
      setLogoSaving(false);
    }
  };

  const handleBannerUpload = async (banner, e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBannerSaving(s => ({ ...s, [banner._id]: true }));
    try {
      const base64 = await toBase64(file);
      await axios.put(`${API}/banners/${banner._id}`, { image: base64 }, { headers: authHeader });
      setBanners(prev => prev.map(b => b._id === banner._id ? { ...b, image: base64 } : b));
      toast.success(`${BANNER_LABELS[banner.type] || banner.type} updated!`);
    } catch (err) {
      toast.error('Failed to update banner');
    } finally {
      setBannerSaving(s => ({ ...s, [banner._id]: false }));
    }
  };

  const handleBannerTextSave = async (bannerId) => {
    setBannerTextSaving(s => ({ ...s, [bannerId]: true }));
    try {
      const payload = bannerTexts[bannerId] || {};
      const { data } = await axios.put(`${API}/banners/${bannerId}`, payload, { headers: authHeader });
      setBanners(prev => prev.map(b => b._id === bannerId ? { ...b, ...data } : b));
      toast.success('Banner text saved!');
    } catch (err) {
      toast.error('Failed to save banner text');
    } finally {
      setBannerTextSaving(s => ({ ...s, [bannerId]: false }));
    }
  };

  const updateBannerText = (bannerId, field, value) => {
    setBannerTexts(prev => ({ ...prev, [bannerId]: { ...prev[bannerId], [field]: value } }));
  };

  const handleCatImageUpload = async (cat, e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCatSaving(s => ({ ...s, [cat._id]: true }));
    try {
      const base64 = await toBase64(file);
      await axios.put(`${API}/categories/${cat._id}`, { image: base64 }, { headers: authHeader });
      setCategories(prev => prev.map(c => c._id === cat._id ? { ...c, image: base64 } : c));
      toast.success(`${cat.name} image updated!`);
    } catch {
      toast.error('Failed to update category image');
    } finally {
      setCatSaving(s => ({ ...s, [cat._id]: false }));
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="admin-media-branding">
      <div>
        <h1 className="font-playfair text-2xl font-bold text-[#1F1410]">Media & Branding</h1>
        <p className="text-[#7A6A63] text-sm mt-0.5">Manage all images and branding assets across your storefront</p>
      </div>

      {/* Logo */}
      <div className="bg-white rounded-2xl border border-[#ECE0DA] p-5 shadow-sm" data-testid="logo-section">
        <h3 className="font-playfair font-semibold text-[#1F1410] mb-1">Site Logo</h3>
        <p className="text-xs text-[#7A6A63] mb-4">Used in navbar and footer on every page. Uploading instantly updates the live site.</p>
        <div className="flex items-center gap-6">
          <div className="w-24 h-24 rounded-2xl bg-[#FDF8F3] border border-[#ECE0DA] flex items-center justify-center overflow-hidden">
            {settings?.logo ? (
              <img src={settings.logo} alt="Current logo" className="w-full h-full object-contain p-2" />
            ) : (
              <div className="text-center p-2">
                <p className="text-xs text-[#7A6A63]">No logo</p>
              </div>
            )}
          </div>
          <div className="flex-1">
            <label className={`inline-flex items-center gap-2 px-4 py-2.5 border border-[#ECE0DA] rounded-full cursor-pointer hover:border-[#7A1125] transition-colors text-sm font-medium ${logoSaving ? 'opacity-50 pointer-events-none' : ''}`} data-testid="logo-upload-button">
              <Upload size={15} className="text-[#7A6A63]" />
              {logoSaving ? 'Saving...' : 'Upload New Logo'}
              <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
            </label>
            {logoError && (
              <div className="flex items-center gap-1 mt-2 text-red-600 text-xs" data-testid="logo-error">
                <AlertCircle size={12} />
                <span>{logoError}</span>
              </div>
            )}
            {settings?.logo && !logoError && (
              <div className="flex items-center gap-1 mt-2 text-green-600 text-xs">
                <CheckCircle size={12} /><span>Logo is live on all pages</span>
              </div>
            )}
            <p className="text-xs text-[#7A6A63] mt-1">PNG, SVG or JPG recommended. Will appear in navbar and footer.</p>
          </div>
        </div>
      </div>

      {/* Page Banners */}
      <div className="bg-white rounded-2xl border border-[#ECE0DA] p-5 shadow-sm" data-testid="banners-section">
        <h3 className="font-playfair font-semibold text-[#1F1410] mb-1">Page Banners</h3>
        <p className="text-xs text-[#7A6A63] mb-4">Edit banner image and text for each page. Changes go live instantly.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {banners.map(banner => {
            const txt = bannerTexts[banner._id] || {};
            const showCta = ['home', 'combo', 'combos', 'menu'].includes(banner.type);
            return (
              <div key={banner._id} className="border border-[#ECE0DA] rounded-xl overflow-hidden" data-testid={`banner-${banner.type}`}>
                {/* Image preview */}
                <div className="relative h-28 bg-[#FDF8F3]">
                  {banner.image ? (
                    <img src={banner.image} alt={BANNER_LABELS[banner.type]} className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#7A6A63] text-xs">No image</div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end justify-between p-2">
                    <span className="text-white text-xs font-semibold">{BANNER_LABELS[banner.type] || banner.type}</span>
                    <label className={`flex items-center gap-1 px-2 py-1 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-lg cursor-pointer transition-colors text-xs font-medium text-white ${bannerSaving[banner._id] ? 'opacity-50 pointer-events-none' : ''}`} data-testid={`replace-banner-${banner.type}`}>
                      <Upload size={11} />
                      {bannerSaving[banner._id] ? 'Saving...' : 'Replace Image'}
                      <input type="file" accept="image/*" onChange={e => handleBannerUpload(banner, e)} className="hidden" />
                    </label>
                  </div>
                </div>
                {/* Text fields */}
                <div className="p-3 space-y-2 bg-[#FDFAF8]">
                  <div>
                    <label className="block text-[10px] font-semibold text-[#7A6A63] uppercase tracking-wide mb-0.5">Headline</label>
                    <input
                      value={txt.headline}
                      onChange={e => updateBannerText(banner._id, 'headline', e.target.value)}
                      placeholder="Banner headline..."
                      className="w-full px-2.5 py-1.5 border border-[#ECE0DA] rounded-lg text-xs focus:outline-none focus:border-[#7A1125]"
                      data-testid={`banner-headline-${banner.type}`}
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-semibold text-[#7A6A63] uppercase tracking-wide mb-0.5">Subtext</label>
                    <input
                      value={txt.subtext}
                      onChange={e => updateBannerText(banner._id, 'subtext', e.target.value)}
                      placeholder="Short description..."
                      className="w-full px-2.5 py-1.5 border border-[#ECE0DA] rounded-lg text-xs focus:outline-none focus:border-[#7A1125]"
                      data-testid={`banner-subtext-${banner.type}`}
                    />
                  </div>
                  {showCta && (
                    <div>
                      <label className="block text-[10px] font-semibold text-[#7A6A63] uppercase tracking-wide mb-0.5">Button Text</label>
                      <input
                        value={txt.ctaText}
                        onChange={e => updateBannerText(banner._id, 'ctaText', e.target.value)}
                        placeholder="e.g. Order Now"
                        className="w-full px-2.5 py-1.5 border border-[#ECE0DA] rounded-lg text-xs focus:outline-none focus:border-[#7A1125]"
                        data-testid={`banner-cta-${banner.type}`}
                      />
                    </div>
                  )}
                  <button
                    onClick={() => handleBannerTextSave(banner._id)}
                    disabled={bannerTextSaving[banner._id]}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-[#7A1125] hover:bg-[#8B1A2B] text-white rounded-lg text-xs font-semibold transition-colors disabled:opacity-50"
                    data-testid={`save-banner-text-${banner.type}`}
                  >
                    <Save size={11} />
                    {bannerTextSaving[banner._id] ? 'Saving...' : 'Save Text'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Category Images */}
      <div className="bg-white rounded-2xl border border-[#ECE0DA] p-5 shadow-sm" data-testid="category-images-section">
        <h3 className="font-playfair font-semibold text-[#1F1410] mb-1">Category Tile Images</h3>
        <p className="text-xs text-[#7A6A63] mb-4">Images shown on the homepage category grid and menu page.</p>
        <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-9 gap-3">
          {categories.map(cat => (
            <div key={cat._id} className="text-center" data-testid={`cat-image-${cat._id}`}>
              <div className="relative w-full aspect-square rounded-xl overflow-hidden bg-[#FDF8F3] border border-[#ECE0DA] mb-1.5">
                {cat.image ? (
                  <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-xl">{cat.emoji}</div>
                )}
                <label className={`absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity cursor-pointer ${catSaving[cat._id] ? 'opacity-100' : ''}`}>
                  <Upload size={16} className="text-white" />
                  <input type="file" accept="image/*" onChange={e => handleCatImageUpload(cat, e)} className="hidden" />
                </label>
              </div>
              <p className="text-xs font-medium text-[#1F1410] truncate">{cat.name}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MediaBranding;
